import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faCalendar, faNewspaper } from '@fortawesome/free-solid-svg-icons'
import {
  formatPublishedDate,
  placeholderImage,
  toExternalImage,
} from '../lib/dropify'
import type { StorefrontBlogPost } from '../lib/dropify'
import { trackNewsSelect } from '../lib/analytics'
import { listDashboardNewsPosts } from '../lib/news'
import { SectionLoadingState } from './LoadingState'
import { NewsListTracker } from './NewsAnalytics'

const NEWS_IMAGE_TRANSFORM = {
  width: 900,
  height: 600,
  smart: true,
  format: 'webp',
  quality: 76,
} as const

export const NEWS_INDEX_PATH = '/berita'

export function newsPostPath(post: StorefrontBlogPost) {
  return `${NEWS_INDEX_PATH}/${encodeURIComponent(post.slug || post.id)}`
}

type NewsCardProps = {
  post: StorefrontBlogPost
  listName?: string
  index?: number
  headingLevel?: 'h2' | 'h3'
}

export function NewsCard({ post, listName = 'Berita', index, headingLevel = 'h3' }: NewsCardProps) {
  const publishedDate = formatPublishedDate(post.published_at)
  const href = newsPostPath(post)
  const Heading = headingLevel
  const selectNews = () => trackNewsSelect(post, listName, index)

  return (
    <article data-news-card className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-blue-200 hover:shadow-xl">
      <a href={href} onClick={selectNews} className="block overflow-hidden bg-slate-100" aria-label={`Baca berita: ${post.title}`}>
        <img
          src={toExternalImage(post.featured_image_url, NEWS_IMAGE_TRANSFORM) || placeholderImage(post.title, 900, 600)}
          alt={post.featured_image_alt || post.title}
          width="900"
          height="600"
          loading="lazy"
          decoding="async"
          className="h-56 w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
      </a>

      <div className="flex flex-1 flex-col p-5 md:p-6">
        <div className="mb-4 flex flex-wrap items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 font-bold text-blue-700">
            <FontAwesomeIcon icon={faNewspaper} className="h-3 w-3" />
            Berita
          </span>
          {publishedDate && (
            <time dateTime={post.published_at || undefined} className="inline-flex items-center gap-1.5 text-slate-500">
              <FontAwesomeIcon icon={faCalendar} className="h-3 w-3" />
              {publishedDate}
            </time>
          )}
        </div>

        <Heading className="line-clamp-3 text-xl font-black leading-tight tracking-tight text-slate-900 transition-colors group-hover:text-blue-700">
          <a href={href} onClick={selectNews}>{post.title}</a>
        </Heading>

        {post.excerpt && (
          <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-slate-600">
            {post.excerpt}
          </p>
        )}

        <a
          href={href}
          onClick={selectNews}
          className="mt-auto inline-flex min-h-11 items-center gap-2 self-start pt-6 text-sm font-bold text-blue-700 transition-colors hover:text-blue-900 focus:outline-none focus:underline"
        >
          Baca berita
          <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
        </a>
      </div>
    </article>
  )
}

export function HomeNewsSection({
  posts,
  variant = 'homepage',
  loading = false,
  failed = false,
}: {
  posts: StorefrontBlogPost[]
  variant?: 'homepage' | 'landing'
  loading?: boolean
  failed?: boolean
}) {
  const listName = variant === 'homepage' ? 'Berita Homepage' : 'Berita Landing Page'

  return (
    <section id="berita" data-home-section={variant === 'homepage' ? 'berita' : undefined} data-ad-section={variant === 'landing' ? 'fulfillment-proof' : undefined} className="relative overflow-hidden bg-slate-50 py-16 md:py-20">
      <NewsListTracker listName={listName} posts={posts} />
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        aria-hidden="true"
        style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(203 213 225 / 0.55) 1px, transparent 0)', backgroundSize: '24px 24px' }}
      />
      <div className="relative mx-auto max-w-7xl px-4">
        <div className="mb-8 flex flex-col gap-5 md:mb-10 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-black leading-tight tracking-tight text-slate-900 md:text-4xl">
              Berita dari Dropify
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 md:text-base">
              Berita berikut mencatat pesanan grosir yang kami tangani: jenis barang, jumlah, dan tujuan pengirimannya. Baca prosesnya untuk melihat cara kami menyiapkan pesanan sampai diserahkan ke ekspedisi.
            </p>
          </div>
          <a
            href={NEWS_INDEX_PATH}
            className="inline-flex min-h-11 items-center gap-2 self-start rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition-colors hover:border-blue-300 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 md:self-auto"
          >
            Lihat semua berita
            <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5" />
          </a>
        </div>

        {posts.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((post, index) => (
              <NewsCard key={post.id} post={post} listName={listName} index={index} />
            ))}
          </div>
        ) : loading ? (
          <SectionLoadingState
            label="Memuat berita pemenuhan pesanan..."
            count={3}
            gridClassName="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
            cardClassName="h-[28rem]"
          />
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white px-6 py-10 text-center text-sm text-slate-600">
            {failed
              ? 'Berita pemenuhan belum dapat dimuat. Coba muat ulang atau buka arsip berita.'
              : 'Berita belum dapat ditampilkan. Buka arsip berita atau muat ulang halaman untuk memeriksa pembaruan.'}
          </div>
        )}
      </div>
    </section>
  )
}

/** Load the same news section on landing pages that have no server-provided posts. */
export function AdNewsProofSection({ posts }: { posts?: StorefrontBlogPost[] }) {
  const [fetchedPosts, setFetchedPosts] = useState<StorefrontBlogPost[]>([])
  const [fetchState, setFetchState] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    posts && posts.length > 0 ? 'ready' : 'idle',
  )

  useEffect(() => {
    if (posts && posts.length > 0) {
      setFetchState('ready')
      return
    }

    let active = true
    setFetchState('loading')
    listDashboardNewsPosts()
      .then((items) => {
        if (!active) return
        setFetchedPosts(items)
        setFetchState('ready')
      })
      .catch(() => {
        if (!active) return
        setFetchedPosts([])
        setFetchState('error')
      })

    return () => {
      active = false
    }
  }, [posts])

  const proofPosts = (posts && posts.length > 0 ? posts : fetchedPosts).slice(0, 3)

  return <HomeNewsSection posts={proofPosts} variant="landing" loading={fetchState === 'idle' || fetchState === 'loading'} failed={fetchState === 'error'} />
}
